// src/docs/pages/DocsContentPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import DocsLayout from '../components/DocsLayout';
import SectionNavigator from '@/components/SectionNavigator';
import { getDocContent } from '../utils/docs';

interface Section {
  id: string;
  title: string;
  level: number;
}

const generateId = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const extractSections = (content: string): Section[] => {
  const lines = content.split('\n');
  const extractedSections: Section[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('# ')) {
      const title = trimmedLine.substring(2);
      const id = generateId(title);
      extractedSections.push({ id, title, level: 1 });
    } else if (trimmedLine.startsWith('## ')) {
      const title = trimmedLine.substring(3);
      const id = generateId(title);
      extractedSections.push({ id, title, level: 2 });
    } else if (trimmedLine.startsWith('### ')) {
      const title = trimmedLine.substring(4);
      const id = generateId(title);
      extractedSections.push({ id, title, level: 3 });
    }
  }
  return extractedSections;
};

const DocsContentPage: React.FC = () => {
  const { topic } = useParams<{ topic: string }>();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    try {
        const fileName = topic || 'Welcome';
        const content = getDocContent(fileName);

        if (!content) {
          setMarkdownContent('# Not Found\n\nThis documentation page could not be found.');
          setSections([]);
        } else {
          setMarkdownContent(content);
          setSections(extractSections(content));
        }
    } catch (e) {
        console.error(e);
        setMarkdownContent('# Error\n\nFailed to load documentation.');
        setSections([]);
    }
  }, [topic]);

  // Custom components to style the markdown output
  const components = {
    h1: ({ ...props }: React.ComponentPropsWithoutRef<'h1'>) => {
      const id = generateId(props.children?.toString() || '');
      return <h1 id={id} className="text-3xl font-bold mt-8 mb-6 scroll-mt-20 font-mono uppercase tracking-tight border-b-4 border-neutral-950 dark:border-neutral-100 pb-2" {...props} />;
    },
    h2: ({ ...props }: React.ComponentPropsWithoutRef<'h2'>) => {
      const id = generateId(props.children?.toString() || '');
      return <h2 id={id} className="text-2xl font-bold mt-8 mb-4 scroll-mt-20 font-mono tracking-tight border-b-2 border-neutral-200 dark:border-neutral-800 pb-1" {...props} />;
    },
    h3: ({ ...props }: React.ComponentPropsWithoutRef<'h3'>) => {
        const id = generateId(props.children?.toString() || '');
        return <h3 id={id} className="text-xl font-bold mt-6 mb-3 scroll-mt-20 font-mono" {...props} />;
    },
    h4: ({ ...props }: React.ComponentPropsWithoutRef<'h4'>) => {
        const id = generateId(props.children?.toString() || '');
        return <h4 id={id} className="text-lg font-bold mt-6 mb-2 scroll-mt-20 font-mono uppercase tracking-wide" {...props} />;
    },
    h5: ({ ...props }: React.ComponentPropsWithoutRef<'h5'>) => {
        const id = generateId(props.children?.toString() || '');
        return <h5 id={id} className="text-base font-bold mt-4 mb-2 scroll-mt-20 font-mono" {...props} />;
    },
    h6: ({ ...props }: React.ComponentPropsWithoutRef<'h6'>) => {
        const id = generateId(props.children?.toString() || '');
        return <h6 id={id} className="text-sm font-bold mt-4 mb-2 scroll-mt-20 font-mono uppercase" {...props} />;
    },
    p: ({ ...props }: React.ComponentPropsWithoutRef<'p'>) => (
      <p className="leading-7 [&:not(:first-child)]:mt-6 text-base text-neutral-800 dark:text-neutral-300 font-mono" {...props} />
    ),
    ul: ({ ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul className="my-6 ml-6 list-disc [&>li]:mt-2 font-mono" {...props} />
    ),
    ol: ({ ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol className="my-6 ml-6 list-decimal [&>li]:mt-2 font-mono" {...props} />
    ),
    li: ({ ...props }: React.ComponentPropsWithoutRef<'li'>) => (
      <li className="text-base text-neutral-800 dark:text-neutral-300 font-mono" {...props} />
    ),
    strong: ({ ...props }: React.ComponentPropsWithoutRef<'strong'>) => (
      <strong className="font-bold text-foreground font-mono" {...props} />
    ),
    em: ({ ...props }: React.ComponentPropsWithoutRef<'em'>) => (
      <em className="italic text-lg font-mono" {...props} />
    ),
    a: ({ ...props }: React.ComponentPropsWithoutRef<'a'>) => (
      <a className="font-medium underline underline-offset-4 decoration-neutral-400 hover:decoration-neutral-950 dark:decoration-neutral-600 dark:hover:decoration-neutral-100 transition-colors cursor-pointer" {...props} />
    ),
    pre: ({ children }: React.ComponentPropsWithoutRef<'pre'>) => <>{children}</>,
    hr: ({ ...props }: React.ComponentPropsWithoutRef<'hr'>) => (
      <hr className="my-8 border-t-2 border-neutral-950 dark:border-neutral-100" {...props} />
    ),
    table: (props: React.ComponentPropsWithoutRef<'table'>) => (
      <div className="my-6 w-full overflow-y-auto">
        <table className="w-full text-left border-collapse border-2 border-neutral-950 dark:border-neutral-100" {...props} />
      </div>
    ),
    th: (props: React.ComponentPropsWithoutRef<'th'>) => (
      <th className="px-4 py-3 border-b-2 border-r border-neutral-950 dark:border-neutral-100 bg-neutral-100 dark:bg-neutral-900 font-mono text-sm font-bold uppercase tracking-wider last:border-r-0" {...props} />
    ),
    td: (props: React.ComponentPropsWithoutRef<'td'>) => (
      <td className="px-4 py-3 border-b border-r border-neutral-950 dark:border-neutral-100 last:border-r-0 border-b-neutral-200 dark:border-b-neutral-800" {...props} />
    ),
    blockquote: ({ children, ...props }: React.ComponentPropsWithoutRef<'blockquote'>) => {
      return (
        <blockquote className="my-6 pl-6 border-l-4 border-neutral-950 dark:border-neutral-100 italic font-mono text-lg" {...props}>
          {children}
        </blockquote>
      );
    },
    code: ({ inline, className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { inline?: boolean }) => {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          style={vscDarkPlus as any}
          language={match[1]}
          PreTag="div"
          className="rounded-none border-2 border-neutral-950 dark:border-neutral-100 !my-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,1)]"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-neutral-200 dark:bg-neutral-800 px-1.5 py-0.5 rounded-sm text-sm font-mono text-neutral-900 dark:text-neutral-100 border border-neutral-400 dark:border-neutral-600" {...props}>
          {children}
        </code>
      );
    },
    img: (props: React.ComponentPropsWithoutRef<'img'>) => (
        <div className="my-6 flex justify-center">
            <img className="max-w-full h-auto rounded-lg shadow-lg border" {...props} />
        </div>
    )
  };

  return (
    <    DocsLayout
      rightSidebar={sections.length > 0 ? <SectionNavigator sections={sections} /> : undefined}
    >
      <div className="max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={components}
        >
          {markdownContent}
        </ReactMarkdown>
      </div>
    </DocsLayout>
  );
};

export default DocsContentPage;
